import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import styles from "./ScheduleReviewTaskActionBar.module.scss";

interface ScheduleReviewConfirmActionBarProps {
    selectedCount: number;
    totalCount: number;
    isBusy: boolean;
    confirmAllLabel: string;
    confirmAllHotkeyDescription: string;
    confirmAllModal: { title: string; question: string; yes: string };
    onConfirmSelected: () => void;
    onConfirmAll: () => void;
}

export const ScheduleReviewConfirmActionBar = ({
    selectedCount,
    totalCount,
    isBusy,
    confirmAllLabel,
    confirmAllHotkeyDescription,
    confirmAllModal,
    onConfirmSelected,
    onConfirmAll,
}: ScheduleReviewConfirmActionBarProps) => {
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const viewOnlyTooltip = useViewOnlyTooltipReason("");
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

    return (
        <>
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <div className={styles.actions}>
                    {selectedCount > 0 && (
                        <Tooltip content={viewOnlyTooltip}>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => {
                                    if (!refuseIfViewOnly()) {
                                        onConfirmSelected();
                                    }
                                }}
                                disabled={isViewOnly || isBusy}
                            >
                                Confirm selected
                            </Button>
                        </Tooltip>
                    )}
                    {totalCount > 0 && (
                        <WithHotkey
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            tooltip={viewOnlyTooltip}
                            hotKeyOptions={{
                                description: confirmAllHotkeyDescription,
                                preventDefault: true,
                            }}
                        >
                            {(hotkeyHint) => (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => {
                                        if (!refuseIfViewOnly()) {
                                            setIsConfirmModalVisible(true);
                                        }
                                    }}
                                    disabled={isViewOnly || isBusy}
                                    rightIcon={hotkeyHint}
                                >
                                    {confirmAllLabel}
                                </Button>
                            )}
                        </WithHotkey>
                    )}
                </div>
            </CommandIsland>

            <ConfirmationModal
                show={isConfirmModalVisible}
                title={confirmAllModal.title}
                question={confirmAllModal.question}
                yes={confirmAllModal.yes}
                no="Cancel"
                onConfirm={() => {
                    setIsConfirmModalVisible(false);
                    onConfirmAll();
                }}
                onReject={() => setIsConfirmModalVisible(false)}
                onHide={() => setIsConfirmModalVisible(false)}
            />
        </>
    );
};
